
import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { Tooltip, Dropdown, Button } from "antd";
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined,
  UnorderedListOutlined, OrderedListOutlined,
  AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined,
  LinkOutlined, UndoOutlined, RedoOutlined, CodeOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import moment from "moment";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL, API_URL, EMAIL_API_URL } from "../../config";

import { placeClickToCall } from "../utils/clickToCall";
import {
  useAdminList,
  useUpdateEnquiryStatus,
  useAllocateLeads,
} from "../hooks/useManageEnquiry";
import {
  fetchLeadDetails,
  allocateLeads,
} from "../services/manageEnquiryService";
import { useEmailTemplates } from "../hooks/useEmailTemplate";
import { fetchTemplatePreview } from "../services/placeholderService";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const FOLLOW_UP_STATUS_OPTIONS = [
  { option_value: "0", label: "Follow Up" },
  { option_value: "1", label: "Interested" },
  { option_value: "2", label: "Not Interested" },
  { option_value: "3", label: "Callback" },
  { option_value: "4", label: "Converted" },
  { option_value: "5", label: "Deal Closed" },
  { option_value: "6", label: "No Response" },
];

const CALL_OUTCOMES = [
  "Connected",
  "No Answer",
  "Voicemail Left",
  "Call Back Later",
  "Wrong Number",
];

// ─── ICONS ───────────────────────────────────────────────────────────────────
const IC = {
  Mail: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  WA: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
  Phone: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .84h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  Note: () => (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Plus: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Send: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Attach: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  ),
  Audio: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
    </svg>
  ),
  Video: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
  Image: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  Doc: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  Check: () => (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  DblCheck: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="17 1 9 9 5 5" />
      <polyline points="22 6 12 16 8 12" />
    </svg>
  ),
  Trash: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  ),
  Negative: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3z" />
      <path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17" />
    </svg>
  ),
  Calendar: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Clock: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  User: () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Building: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <line x1="8" y1="21" x2="8" y2="3" />
      <line x1="16" y1="21" x2="16" y2="3" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  ),
  MapPin: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  X: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Filter: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  ArrowLeft: () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  Edit: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  PhoneAdd: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .84h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
      <line x1="19" y1="4" x2="19" y2="10" />
      <line x1="16" y1="7" x2="22" y2="7" />
    </svg>
  ),
  ChevronDown: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Layers: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  CC: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8" />
      <path d="M22 8l-6-4" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Eye: () => (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getTypeCfg = (t) =>
  ({
    email: {
      color: "#667eea",
      bg: "#f0f4ff",
      border: "#c7d2fe",
      label: "Email",
      Icon: IC.Mail,
    },
    whatsapp: {
      color: "#16a34a",
      bg: "#f0fdf4",
      border: "#bbf7d0",
      label: "WhatsApp",
      Icon: IC.WA,
    },
    call: {
      color: "#f59e0b",
      bg: "#fffbeb",
      border: "#fde68a",
      label: "Call",
      Icon: IC.Phone,
    },
    note: {
      color: "#8b5cf6",
      bg: "#f5f3ff",
      border: "#ddd6fe",
      label: "Note",
      Icon: IC.Note,
    },
    meeting: {
      color: "#0284c7",
      bg: "#f0f9ff",
      border: "#bae6fd",
      label: "Meeting",
      Icon: IC.Calendar,
    },
    other: {
      color: "#8b5cf6",
      bg: "#f5f3ff",
      border: "#ddd6fe",
      label: "Note",
      Icon: IC.Note,
    },
  })[t] || {
    color: "#6b7280",
    bg: "#f9fafb",
    border: "#e5e7eb",
    label: t,
    Icon: IC.User,
  };

const getMediaIcon = (t) =>
  ({
    audio: <IC.Audio />,
    video: <IC.Video />,
    image: <IC.Image />,
    document: <IC.Doc />,
  })[t] || null;

const parseAttachments = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val.map((item) => {
      if (typeof item === "string") {
        const fileName =
          decodeURIComponent(item.split("/").pop().split("?")[0]) ||
          "Attachment";
        return { name: fileName, url: item };
      }
      if (typeof item === "object" && item !== null) {
        return {
          name:
            item.name ||
            (item.url
              ? decodeURIComponent(item.url.split("/").pop().split("?")[0])
              : "Attachment"),
          url: item.url || "",
          size: item.size || "",
        };
      }
      return { name: "Attachment", url: "" };
    });
  }
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return parseAttachments(Array.isArray(parsed) ? parsed : [parsed]);
    } catch {
      const fileName =
        decodeURIComponent(val.split("/").pop().split("?")[0]) || "Attachment";
      return [{ name: fileName, url: val }];
    }
  }
  return [];
};

const getFileType = (nameOrUrl) => {
  if (!nameOrUrl) return "other";
  const ext = nameOrUrl.split(".").pop().split("?")[0].toLowerCase();
  if (
    ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico", "tiff"].includes(
      ext,
    )
  )
    return "image";
  if (["mp4", "webm", "mov", "avi", "mkv", "m4v", "ogv"].includes(ext))
    return "video";
  if (["mp3", "wav", "ogg", "aac", "m4a", "flac", "wma"].includes(ext))
    return "audio";
  if (["pdf"].includes(ext)) return "pdf";
  if (["doc", "docx"].includes(ext)) return "word";
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (["ppt", "pptx"].includes(ext)) return "ppt";
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "archive";
  return "other";
};

const FileTypeIcon = ({ type }) => {
  const s = { width: "18px", height: "18px" };
  if (type === "pdf")
    return (
      <span
        style={{
          ...s,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          fontWeight: 800,
          color: "#dc2626",
          background: "#fef2f2",
          borderRadius: "4px",
          border: "1px solid #fecaca",
        }}
      >
        PDF
      </span>
    );
  if (type === "word")
    return (
      <span
        style={{
          ...s,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "8px",
          fontWeight: 800,
          color: "#2563eb",
          background: "#eff6ff",
          borderRadius: "4px",
          border: "1px solid #bfdbfe",
        }}
      >
        DOC
      </span>
    );
  if (type === "excel")
    return (
      <span
        style={{
          ...s,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "8px",
          fontWeight: 800,
          color: "#16a34a",
          background: "#f0fdf4",
          borderRadius: "4px",
          border: "1px solid #bbf7d0",
        }}
      >
        XLS
      </span>
    );
  if (type === "video") return <IC.Video />;
  if (type === "audio") return <IC.Audio />;
  if (type === "archive") return <IC.Doc />;
  return <IC.Doc />;
};

const forceDownload = async (url, filename) => {
  const name =
    filename ||
    decodeURIComponent(url.split("/").pop().split("?")[0]) ||
    "download";
  try {
    const res = await fetch(url, { mode: "cors" });
    if (res.ok) {
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 150);
      return;
    }
  } catch {}
  try {
    const token = localStorage.getItem("cms_token");
    const proxyUrl = `${process.env.REACT_APP_API_BASE_URL}/download-file?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 150);
      return;
    }
  } catch {}
  window.open(url, "_blank");
};

const statusColor = (s) => {
  const m = {
    "No Response": "#9ca3af",
    Interested: "#16a34a",
    "Not Interested": "#6b7280",
    "Meeting Scheduled": "#0284c7",
    Converted: "#15803d",
    Closed: "#6b7280",
    "Follow Up": "#667eea",
    "Not Responding": "#9ca3af",
    "Hot Lead": "#ef4444",
    "Mark for Future": "#f59e0b",
    Negative: "#dc2626",
    pending: "#f59e0b",
    completed: "#16a34a",
    cancelled: "#ef4444",
    rescheduled: "#0891b2",
  };
  return m[s] || "#667eea";
};

// ─── CUSTOM FOLLOW-UP STATUS SELECT ──────────────────────────────────────────
const FollowUpStatusSelect = ({
  value,
  onChange,
  followUpOptionsList = [],
  style = {},
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const selected = followUpOptionsList.find((o) => o.option_value === value);
  const color = statusColor(selected?.label || "");

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          padding: "9px 36px 9px 12px",
          border: `1.5px solid ${color}`,
          borderRadius: "9px",
          fontSize: "13px",
          fontWeight: 700,
          color: color,
          background: `${color}10`,
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxSizing: "border-box",
          transition: "all 0.2s",
          boxShadow: open ? `0 0 0 3px ${color}20` : "none",
          position: "relative",
        }}
      >
        <span style={{ flex: 1 }}>{selected?.label || "Select status"}</span>
        <span
          style={{
            position: "absolute",
            right: "11px",
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
            transition: "transform 0.2s",
            color: color,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
          }}
        >
          <IC.ChevronDown />
        </span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "white",
            border: "1.5px solid #e8ecff",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(102,126,234,0.18)",
            overflow: "hidden",
          }}
        >
          <div
            style={{ maxHeight: "220px", overflowY: "auto", padding: "6px" }}
          >
            {followUpOptionsList.map((opt) => {
              const c = statusColor(opt.label);
              const isActive = opt.option_value === value;
              return (
                <button
                  key={opt.option_value}
                  type="button"
                  onClick={() => {
                    onChange(opt.option_value);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "white" : c,
                    background: isActive ? c : "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    transition: "all 0.15s",
                    marginBottom: "2px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = `${c}15`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {opt.label}
                  {isActive && (
                    <span
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <IC.Check />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AudioWave = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "2px",
      height: "20px",
    }}
  >
    {[1, 1.6, 0.8, 1.4, 0.6, 1.2, 1, 1.5, 0.7, 1.3].map((h, i) => (
      <div
        key={i}
        style={{
          width: "3px",
          borderRadius: "2px",
          background: "currentColor",
          opacity: 0.7,
          height: `${h * 10}px`,
          animation: `wave ${0.5 + i * 0.1}s ease-in-out infinite alternate`,
        }}
      />
    ))}
    <style>{`@keyframes wave{from{transform:scaleY(0.4)}to{transform:scaleY(1)}}`}</style>
  </div>
);

const nowISO = () => moment().format("YYYY-MM-DD HH:mm:ss");
const createdBy = () => Number(localStorage.getItem("trade_user_id")) || 1;

// ─── Rich text editor for email body (no-template mode) ───────────────────────
function EmailRichEditor({ value, onChange }) {
  const editorRef = React.useRef(null);
  const [showHtml, setShowHtml] = React.useState(false);
  const [htmlValue, setHtmlValue] = React.useState("");
  const isInternal = React.useRef(false);
  const pendingHtml = React.useRef(null);

  React.useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || "") && !isInternal.current)
      editorRef.current.innerHTML = value || "";
    isInternal.current = false;
  }, [value]);

  React.useEffect(() => {
    if (!showHtml && pendingHtml.current !== null && editorRef.current) {
      editorRef.current.innerHTML = pendingHtml.current;
      pendingHtml.current = null;
      emit();
    }
  }, [showHtml]); // eslint-disable-line

  const emit = React.useCallback(() => {
    if (!editorRef.current) return;
    isInternal.current = true;
    onChange?.(editorRef.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, val = null) => { editorRef.current?.focus(); document.execCommand(cmd, false, val); emit(); };

  const btn = (icon, cmd, val, title) => (
    <Tooltip title={title} key={cmd + (val || "")}>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); exec(cmd, val); }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13, color: "#374151", display: "inline-flex", alignItems: "center" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
        onMouseLeave={(e) => e.currentTarget.style.background = "none"}
      >{icon}</button>
    </Tooltip>
  );

  return (
    <div style={{ border: "1px solid #d9d9d9", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, padding: "6px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
        {btn(<BoldOutlined />, "bold", null, "Bold")}
        {btn(<ItalicOutlined />, "italic", null, "Italic")}
        {btn(<UnderlineOutlined />, "underline", null, "Underline")}
        <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
        {btn(<UnorderedListOutlined />, "insertUnorderedList", null, "Bullet list")}
        {btn(<OrderedListOutlined />, "insertOrderedList", null, "Numbered list")}
        <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
        {btn(<AlignLeftOutlined />, "justifyLeft", null, "Align left")}
        {btn(<AlignCenterOutlined />, "justifyCenter", null, "Align center")}
        {btn(<AlignRightOutlined />, "justifyRight", null, "Align right")}
        <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
        <Tooltip title="Insert link">
          <button type="button"
            onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Enter URL"); if (url) exec("createLink", url); }}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 7px", borderRadius: 4, fontSize: 13, color: "#374151", display: "inline-flex", alignItems: "center" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
            onMouseLeave={(e) => e.currentTarget.style.background = "none"}
          ><LinkOutlined /></button>
        </Tooltip>
        {btn(<UndoOutlined />, "undo", null, "Undo")}
        {btn(<RedoOutlined />, "redo", null, "Redo")}
        <div style={{ flex: 1 }} />
        <Button size="small" icon={<CodeOutlined />} onClick={() => {
          if (!showHtml) setHtmlValue(editorRef.current?.innerHTML || "");
          else pendingHtml.current = htmlValue;
          setShowHtml(v => !v);
        }} style={{ fontSize: 11, borderRadius: 6, background: showHtml ? "#1a1a2e" : "transparent", color: showHtml ? "#fff" : "#374151", border: "1px solid #d9d9d9" }}>
          {showHtml ? "Hide HTML" : "View HTML"}
        </Button>
      </div>
      {showHtml
        ? <textarea value={htmlValue} onChange={(e) => setHtmlValue(e.target.value)} rows={8}
            style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", border: "none", outline: "none", fontFamily: "monospace", fontSize: 12, lineHeight: 1.6, color: "#1a1a2e", resize: "vertical", background: "#f8fafc" }} />
        : <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={emit}
            style={{ minHeight: 150, padding: "12px 14px", outline: "none", fontSize: 13, lineHeight: 1.8, color: "#1a1a2e", overflowY: "auto" }} />
      }
    </div>
  );
}

const formatDateTime = (utcDate) => {
  if (!utcDate) return "";
  return moment.utc(utcDate).local().format("DD MMM YYYY, hh:mm A");
};

const formatNextFollowUp = (fu) => {
  if (!fu.next_followup_date) return "";
  const raw = fu.next_followup_date;
  const isUTC = raw.includes("T") || raw.includes("Z");
  const datePart = isUTC
    ? moment.utc(raw).local().format("DD MMM YYYY")
    : moment(raw, "YYYY-MM-DD").format("DD MMM YYYY");
  if (fu.next_followup_time) {
    const timePart = moment(fu.next_followup_time, "HH:mm").format("hh:mm A");
    return `${datePart}, ${timePart}`;
  }
  if (isUTC) return moment.utc(raw).local().format("DD MMM YYYY, hh:mm A");
  return datePart;
};

// ─── STAGE UPDATE MODAL ──────────────────────────────────────────────────────
const StageUpdateModal = ({
  currentStatus,
  initialStatus,
  onSave,
  onCancel,
  isLoading,
  followUpOptionsList = [],
}) => {
  const [newStatus, setNewStatus] = useState(initialStatus || currentStatus);
  const [remarks, setRemarks] = useState("");
  const fromColor = statusColor(currentStatus);
  const toColor = statusColor(newStatus);
  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.5)",
        backdropFilter: "blur(5px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "440px",
          boxShadow: "0 28px 64px rgba(0,0,0,0.22)",
          overflow: "hidden",
          animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <style>{`@keyframes modalIn{from{opacity:0;transform:scale(0.92) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
        <div
          style={{
            background: "linear-gradient(135deg,#667eea,#764ba2)",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "34px",
                height: "34px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <IC.Layers />
            </div>
            <div>
              <div
                style={{ color: "white", fontWeight: 700, fontSize: "15px" }}
              >
                Update Lead Stage
              </div>
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IC.X />
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "18px",
              padding: "12px 14px",
              background: "#f8f9ff",
              borderRadius: "12px",
              border: "1.5px solid #e8ecff",
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#9ca3af",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "5px",
                }}
              >
                Current
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  background: `${fromColor}18`,
                  color: fromColor,
                  fontSize: "12px",
                  fontWeight: 700,
                  border: `1.5px solid ${fromColor}30`,
                }}
              >
                {currentStatus}
              </span>
            </div>
            <div style={{ color: "#a5b4fc", fontSize: "16px" }}>›</div>
            <div style={{ flex: 1, textAlign: "right" }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "#9ca3af",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "5px",
                }}
              >
                New
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  background: `${toColor}18`,
                  color: toColor,
                  fontSize: "12px",
                  fontWeight: 700,
                  border: `1.5px solid ${toColor}30`,
                }}
              >
                {newStatus}
              </span>
            </div>
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>
              Stage <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <select
                style={{
                  width: "100%",
                  padding: "9px 36px 9px 12px",
                  border: `1.5px solid ${toColor}`,
                  borderRadius: "9px",
                  fontSize: "13px",
                  outline: "none",
                  background: "white",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  color: toColor,
                  fontWeight: 700,
                  appearance: "none",
                  WebkitAppearance: "none",
                }}
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                {followUpOptionsList.map((opt) => (
                  <option key={opt.option_value} value={opt.label}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div
                style={{
                  position: "absolute",
                  right: "11px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: toColor,
                  pointerEvents: "none",
                }}
              >
                <IC.ChevronDown />
              </div>
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label style={L}>
              Remarks{" "}
              <span
                style={{
                  color: "#9ca3af",
                  fontWeight: 400,
                  fontSize: "10px",
                  textTransform: "none",
                }}
              >
                (optional)
              </span>
            </label>
            <textarea
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1.5px solid #e2e8f0",
                borderRadius: "9px",
                fontSize: "13px",
                outline: "none",
                resize: "vertical",
                minHeight: "76px",
                background: "white",
                fontFamily: "inherit",
                boxSizing: "border-box",
                lineHeight: 1.6,
              }}
              placeholder="Reason for this change..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              style={{
                flex: 1,
                padding: "10px",
                background: "#f1f5f9",
                color: "#6b7280",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              disabled={isLoading}
              onClick={() =>
                onSave({
                  follow_up_status: newStatus,
                  ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
                })
              }
              style={{
                flex: 2,
                padding: "10px",
                background: `linear-gradient(135deg,${toColor},${toColor}cc)`,
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                boxShadow: `0 4px 14px ${toColor}40`,
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <IC.Check />
              {isLoading ? "Updating..." : "Update Stage"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── FILE CHIP COMPONENT ─────────────────────────────────────────────────────
const FileChip = ({ file, onRemove }) => {
  const name = file instanceof File ? file.name : file.name || "Attachment";
  const rawSize =
    file instanceof File ? file.size : file.size ? Number(file.size) : 0;
  const sizeLabel = rawSize
    ? rawSize > 1024 * 1024
      ? `${(rawSize / (1024 * 1024)).toFixed(1)} MB`
      : `${(rawSize / 1024).toFixed(0)} KB`
    : "";
  const fileUrl = file instanceof File ? URL.createObjectURL(file) : file.url;
  const isImage =
    file instanceof File
      ? file.type?.startsWith("image/")
      : /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(name);
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 10px",
        background: "#f0f4ff",
        border: "1px solid #c7d2fe",
        borderRadius: "8px",
        fontSize: "11px",
        color: "#667eea",
        fontWeight: 600,
        maxWidth: "260px",
      }}
    >
      {isImage && fileUrl ? (
        <img
          src={fileUrl}
          alt={name}
          style={{
            width: "28px",
            height: "28px",
            borderRadius: "5px",
            objectFit: "cover",
            flexShrink: 0,
            border: "1px solid #c7d2fe",
          }}
        />
      ) : (
        <IC.Doc />
      )}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "1px",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "11px",
            fontWeight: 600,
            color: "#374151",
          }}
        >
          {name}
        </span>
        {sizeLabel && (
          <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 400 }}>
            {sizeLabel}
          </span>
        )}
      </div>
      {file.url && (
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          style={{
            color: "#667eea",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            textDecoration: "none",
            fontSize: "13px",
            fontWeight: 700,
          }}
          title="Download"
        >
          ↓
        </a>
      )}
      {onRemove && (
        <button
          onClick={onRemove}
          style={{
            background: "none",
            border: "none",
            color: "#dc2626",
            cursor: "pointer",
            padding: "2px",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            borderRadius: "4px",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#fef2f2")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
        >
          <IC.X />
        </button>
      )}
    </div>
  );
};

// ─── EDIT FOLLOW-UP MODAL ────────────────────────────────────────────────────
const EditFollowUpModal = ({
  fu,
  onSave,
  onCancel,
  isLoading,
  followUpOptionsList = [],
}) => {
  const fileInputRef = useRef();
  const supportsAttachments =
    fu.followup_type === "email" || fu.followup_type === "whatsapp";
  const existingAttachments = parseAttachments(fu.attachments);

  const getInitialDate = () => {
    if (!fu.next_followup_date) return "";
    const raw = fu.next_followup_date;
    if (raw.includes("T") || raw.includes("Z"))
      return moment.utc(raw).local().format("YYYY-MM-DD");
    return raw.slice(0, 10);
  };

  const getInitialTime = () => {
    if (fu.next_followup_time) return fu.next_followup_time;
    if (!fu.next_followup_date) return "";
    return moment.utc(fu.next_followup_date).local().format("HH:mm");
  };

  const [data, setData] = useState({
    enquiry_status: String(fu?.enquiry_status ?? "0"),
    remarks: fu?.remarks || "",
    next_followup_date: getInitialDate(),
    next_followup_time: getInitialTime(),
  });

  useEffect(() => {
    if (followUpOptionsList.length === 0) return;
    setData((prev) => {
      if (
        followUpOptionsList.find(
          (o) => o.option_value === String(prev.enquiry_status),
        )
      ) {
        return prev;
      }
      const match = followUpOptionsList.find(
        (o) => o.label === prev.enquiry_status,
      );
      return { ...prev, enquiry_status: match ? match.option_value : "0" };
    });
  }, [followUpOptionsList]);

  const [keptAttachments, setKeptAttachments] = useState(existingAttachments);
  const [newFiles, setNewFiles] = useState([]);

  const removeKeptAttachment = (idx) =>
    setKeptAttachments((p) => p.filter((_, i) => i !== idx));
  const addNewFiles = (e) => {
    if (!e.target.files?.length) return;
    setNewFiles((p) => [...p, ...Array.from(e.target.files)]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeNewFile = (idx) =>
    setNewFiles((p) => p.filter((_, i) => i !== idx));

  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  const inp = {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "9px",
    fontSize: "13px",
    outline: "none",
    background: "white",
    boxSizing: "border-box",
  };
  const cfg = getTypeCfg(fu.followup_type);

  const handleSave = () => {
    const payload = {
      id: fu.id,
      lead_id: fu.lead_id,
      enquiry_status: data.enquiry_status,
      remarks: data.remarks,
      ...(data.next_followup_date
        ? { next_followup_date: data.next_followup_date }
        : {}),
      ...(data.next_followup_time
        ? { next_followup_time: data.next_followup_time }
        : {}),
    };
    if (supportsAttachments) {
      if (keptAttachments.length > 0)
        payload.existing_attachments = JSON.stringify(keptAttachments);
      if (newFiles.length > 0) payload.attachments = newFiles;
    }
    onSave(payload);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "440px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          overflow: "auto",
          maxHeight: "90vh",
          animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <div
          style={{
            background:
              cfg.color === "#667eea"
                ? "linear-gradient(135deg,#667eea,#764ba2)"
                : `linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <cfg.Icon />
            </div>
            <div>
              <span
                style={{ color: "white", fontWeight: 700, fontSize: "15px" }}
              >
                Edit {cfg.label} Follow-Up
              </span>
              <div
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: "11px",
                  marginTop: "2px",
                }}
              >
                {formatDateTime(fu.followup_date)}
              </div>
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IC.X />
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Follow-up Status</label>
            <FollowUpStatusSelect
              value={data.enquiry_status}
              followUpOptionsList={followUpOptionsList}
              onChange={(val) =>
                setData((p) => ({ ...p, enquiry_status: val }))
              }
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>
              <IC.Note /> Remarks
            </label>
            <textarea
              style={{
                ...inp,
                resize: "vertical",
                minHeight: "80px",
                fontFamily: "inherit",
                lineHeight: 1.6,
              }}
              placeholder="Update remarks..."
              value={data.remarks}
              onChange={(e) =>
                setData((p) => ({ ...p, remarks: e.target.value }))
              }
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "14px",
            }}
          >
            <div>
              <label style={L}>
                <IC.Calendar /> Next Follow-up
              </label>
              <input
                type="date"
                style={inp}
                value={data.next_followup_date}
                onChange={(e) =>
                  setData((p) => ({ ...p, next_followup_date: e.target.value }))
                }
              />
            </div>
            <div>
              <label style={L}>
                <IC.Clock /> Time
              </label>
              <input
                type="time"
                style={inp}
                value={data.next_followup_time}
                onChange={(e) =>
                  setData((p) => ({ ...p, next_followup_time: e.target.value }))
                }
                onClick={(e) => e.target.showPicker?.()}
              />
            </div>
          </div>
          {supportsAttachments && (
            <div style={{ marginBottom: "20px" }}>
              <label style={L}>
                <IC.Attach /> Attachments
              </label>
              {keptAttachments.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#9ca3af",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      marginBottom: "5px",
                    }}
                  >
                    Existing Files
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                  >
                    {keptAttachments.map((att, i) => (
                      <FileChip
                        key={`e-${i}`}
                        file={{
                          name:
                            att.name ||
                            att.url?.split("/").pop() ||
                            "Attachment",
                          url: att.url,
                          size: att.size ? Number(att.size) : 0,
                        }}
                        onRemove={() => removeKeptAttachment(i)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {newFiles.length > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#16a34a",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      marginBottom: "5px",
                    }}
                  >
                    New Files
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                  >
                    {newFiles.map((f, i) => (
                      <FileChip
                        key={`n-${i}`}
                        file={f}
                        onRemove={() => removeNewFile(i)}
                      />
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "7px 14px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: `1.5px solid ${cfg.border}`,
                  background: cfg.bg,
                  color: cfg.color,
                  marginTop: "4px",
                }}
              >
                <IC.Attach /> Add Files
              </button>
              <input
                type="file"
                multiple
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={addNewFiles}
              />
            </div>
          )}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              style={{
                flex: 1,
                padding: "10px",
                background: "#f1f5f9",
                color: "#6b7280",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              disabled={isLoading}
              onClick={handleSave}
              style={{
                flex: 2,
                padding: "10px",
                background: `linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                boxShadow: `0 4px 14px ${cfg.color}40`,
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <IC.Check />
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── CONTACT EDIT MODAL ──────────────────────────────────────────────────────
const ContactEditModal = ({ contact, onSave, onCancel, isLoading }) => {
  const [data, setData] = useState({ ...contact });
  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  const inp = {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "9px",
    fontSize: "13px",
    outline: "none",
    background: "white",
    boxSizing: "border-box",
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "18px",
          width: "100%",
          maxWidth: "420px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          overflow: "hidden",
          animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg,#667eea,#764ba2)",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <IC.User />
            </div>
            <span style={{ color: "white", fontWeight: 700, fontSize: "15px" }}>
              Edit Contact
            </span>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IC.X />
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Full Name</label>
            <input
              style={inp}
              value={data.name}
              onChange={(e) => setData((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Email</label>
            <input
              style={inp}
              type="email"
              value={data.email}
              onChange={(e) =>
                setData((p) => ({ ...p, email: e.target.value }))
              }
            />
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={L}>Phone</label>
            <input
              style={{
                ...inp,
                ...(data.phone &&
                data.phone.length > 0 &&
                data.phone.length < 10
                  ? { borderColor: "#f59e0b" }
                  : {}),
              }}
              value={data.phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                setData((p) => ({ ...p, phone: val }));
              }}
              placeholder="10-digit phone number"
              maxLength={10}
            />
            {data.phone && data.phone.length > 0 && data.phone.length < 10 && (
              <div
                style={{ fontSize: "10px", color: "#f59e0b", marginTop: "4px" }}
              >
                Phone must be 10 digits ({data.phone.length}/10)
              </div>
            )}
          </div>
          <div style={{ marginBottom: "22px" }}>
            <label
              style={{
                ...L,
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <IC.PhoneAdd /> Alt Number{" "}
              <span
                style={{
                  color: "#9ca3af",
                  fontWeight: 400,
                  fontSize: "10px",
                  textTransform: "none",
                }}
              >
                (optional)
              </span>
            </label>
            <input
              style={{
                ...inp,
                ...(data.additional_phone &&
                data.additional_phone === data.phone
                  ? { borderColor: "#ef4444" }
                  : data.additional_phone &&
                      data.additional_phone.length > 0 &&
                      data.additional_phone.length < 10
                    ? { borderColor: "#f59e0b" }
                    : {}),
              }}
              value={data.additional_phone || ""}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                setData((p) => ({ ...p, additional_phone: val }));
              }}
              placeholder="10-digit alt number"
              maxLength={10}
            />
            {data.additional_phone && data.additional_phone === data.phone && (
              <div
                style={{ fontSize: "10px", color: "#ef4444", marginTop: "4px" }}
              >
                Alt number cannot be same as primary phone
              </div>
            )}
            {data.additional_phone &&
              data.additional_phone.length > 0 &&
              data.additional_phone.length < 10 &&
              data.additional_phone !== data.phone && (
                <div
                  style={{
                    fontSize: "10px",
                    color: "#f59e0b",
                    marginTop: "4px",
                  }}
                >
                  Phone must be 10 digits ({data.additional_phone.length}/10)
                </div>
              )}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={onCancel}
              disabled={isLoading}
              style={{
                flex: 1,
                padding: "10px",
                background: "#f1f5f9",
                color: "#6b7280",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (data.phone && data.phone.length !== 10) {
                  return;
                }
                if (
                  data.additional_phone &&
                  data.additional_phone.length > 0 &&
                  data.additional_phone.length !== 10
                ) {
                  return;
                }
                if (
                  data.additional_phone &&
                  data.additional_phone === data.phone
                ) {
                  return;
                }
                onSave(data);
              }}
              disabled={
                isLoading ||
                (data.phone && data.phone.length !== 10) ||
                (data.additional_phone &&
                  data.additional_phone.length > 0 &&
                  data.additional_phone.length !== 10) ||
                (data.additional_phone && data.additional_phone === data.phone)
              }
              style={{
                flex: 2,
                padding: "10px",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
                boxShadow: "0 4px 14px rgba(102,126,234,0.35)",
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <IC.Check />
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── EMAIL PREVIEW MODAL ─────────────────────────────────────────────────────
const EmailPreviewModal = ({ subject, body, onClose }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(15,23,42,0.5)",
      backdropFilter: "blur(5px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
    }}
  >
    <div
      style={{
        background: "white",
        borderRadius: "18px",
        width: "100%",
        maxWidth: "700px",
        boxShadow: "0 28px 64px rgba(0,0,0,0.22)",
        overflow: "hidden",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg,#667eea,#764ba2)",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              background: "rgba(255,255,255,0.2)",
              borderRadius: "9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <IC.Eye />
          </div>
          <div>
            <div style={{ color: "white", fontWeight: 700, fontSize: "14px" }}>
              Template Preview
            </div>
            <div
              style={{
                color: "rgba(255,255,255,.7)",
                fontSize: "11px",
                marginTop: "1px",
              }}
            >
              {subject}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            width: "28px",
            height: "28px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <IC.X />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0" }}>
        <div
          dangerouslySetInnerHTML={{ __html: body }}
          style={{ minHeight: "200px" }}
        />
      </div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const CustomerFollowUp = ({
  enquiry,
  onClose,
  onDeleteLead,
  onNegativeClosed,
}) => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const urlName     = searchParams.get("name") || "";
  const urlEmail    = searchParams.get("email") || "";
  const urlMobile   = searchParams.get("mobile") || "";
  const urlPlanName = searchParams.get("planName") || "";
  const urlPlanType = searchParams.get("planType") || "";
  const urlIsAtty          = searchParams.get("isAttorney");
  const urlOrderId         = searchParams.get("orderId") || "";
  const urlAlternateNumber = searchParams.get("alternateNumber") || "";

  const lead = enquiry || {
    id: customerId || 0,
    name: urlName || "Customer",
    service: urlPlanName || "Enquiry",
    email: urlEmail,
    phone: urlMobile,
    additional_phone: urlAlternateNumber,
    status: "Neutral",
    assignedTo: "Unassigned",
    plan_name: urlPlanName,
    plan_type: urlPlanType,
    is_attorney: urlIsAtty !== "" && urlIsAtty !== null ? Number(urlIsAtty) : null,
    order_id: urlOrderId,
  };
  const handleClose = onClose || (() => navigate(-1));
  const queryClient = useQueryClient();

  const [activePanel, setActivePanel] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [followUpStatus, setFollowUpStatus] = useState(
    lead.lead_stage || lead.follow_up_status || "Follow Up",
  );
  const [showStageModal, setShowStageModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [editingFU, setEditingFU] = useState(null);
  const [showContactEdit, setShowContactEdit] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contact, setContact] = useState({
    name: lead.name,
    email: lead.email,
    phone: lead.phone,
    additional_phone: lead.additional_phone || "",
  });

  const [emailTo, setEmailTo] = useState(lead.email || "");
  const [emailCcList, setEmailCcList] = useState([]);
  const [emailCcInput, setEmailCcInput] = useState("");
  const [emailCcError, setEmailCcError] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailFiles, setEmailFiles] = useState([]);
  const [templateAttachments, setTemplateAttachments] = useState([]);
  const [emailNextDate, setEmailNextDate] = useState("");
  const [emailNextTime, setEmailNextTime] = useState("");
  const [emailRemarks, setEmailRemarks] = useState("");
  const [emailFuStatus, setEmailFuStatus] = useState("0");
  const [showPreview, setShowPreview] = useState(false);

  const [waBody, setWaBody] = useState("");
  const [waFiles, setWaFiles] = useState([]);
  const [waNextDate, setWaNextDate] = useState("");
  const [waNextTime, setWaNextTime] = useState("");
  const [waRemarks, setWaRemarks] = useState("");
  const [waFuStatus, setWaFuStatus] = useState("pending");

  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callNote, setCallNote] = useState("");
  const [callDuration, setCallDuration] = useState("");
  const [callNextDate, setCallNextDate] = useState("");
  const [callNextTime, setCallNextTime] = useState("");
  const [callFuStatus, setCallFuStatus] = useState("0");
  const [noteText, setNoteText] = useState("");
  const [noteNextDate, setNoteNextDate] = useState("");
  const [noteNextTime, setNoteNextTime] = useState("");
  const [showEmailCc, setShowEmailCc] = useState(false);
  const [showEmailBcc, setShowEmailBcc] = useState(false);
  const [emailBccList, setEmailBccList] = useState([]);
  const [emailBccInput, setEmailBccInput] = useState("");
  const [emailBccError, setEmailBccError] = useState("");

  const emailFileRef = useRef();
  const waFileRef = useRef();
  const composeScrollRef = useRef();
  const timelineScrollRef = useRef();

  // ── Hooks ──────────────────────────────────────────────────────────────────
  const { data: adminList = [] } = useAdminList();

  const [expandedIds, setExpandedIds] = useState(new Set());
  const topItemIdRef = useRef(null);

  const [inboundEmails, setInboundEmails] = useState([]);
  const [emailThreadLoading, setEmailThreadLoading] = useState(true);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);

  const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("cms_token")}` });

  const fetchThread = useCallback(async (convId) => {
    const id = convId || activeConversationId;
    if (!id || !lead?.id) return;
    try {
      const internalUserIdFT = Number(localStorage.getItem("trade_user_id"));
      const res = await axios.get(
        `${API_BASE_URL}/subscription-inbox/thread/${lead.id}`,
        { params: { conversation_id: id, internal_user_id: internalUserIdFT }, headers: authH() },
      );
      setInboundEmails(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { /* silent */ }
  }, [lead?.id, activeConversationId]);

  const loadConversations = useCallback(async () => {
    if (!lead?.id) return;
    setEmailThreadLoading(true);
    try {
      const internalUserIdLC = Number(localStorage.getItem("trade_user_id"));
      const res = await axios.get(
        `${API_BASE_URL}/subscription-inbox/conversations/${lead.id}`,
        { params: { internal_user_id: internalUserIdLC }, headers: authH() },
      );
      const convs = Array.isArray(res.data?.data) ? res.data.data : [];
      setConversations(convs);
      if (convs.length > 0) {
        const latestId = convs[0].conversation_id;
        setActiveConversationId(latestId);
        const threadRes = await axios.get(
          `${API_BASE_URL}/subscription-inbox/thread/${lead.id}`,
          { params: { conversation_id: latestId, internal_user_id: internalUserIdLC }, headers: authH() },
        );
        setInboundEmails(Array.isArray(threadRes.data?.data) ? threadRes.data.data : []);
      }
    } catch { /* silent */ }
    setEmailThreadLoading(false);
  }, [lead?.id]);

  useEffect(() => { loadConversations(); }, [lead?.id]);

  const inboundItems = useMemo(() =>
    inboundEmails.map((m) => ({
      id: `email_${m.id}`,
      followup_type: "email",
      followup_date: m.sent_at || m.created_at || null,
      direction: m.direction === "inbound" ? "inbound" : "outbound",
      subject: m.subject || "(No subject)",
      content: m.body || "",
      created_by_name: m.direction === "inbound" ? (m.from_email || "Customer") : "Admin",
      delivery_status: m.status === "failed" ? "failed" : (m.direction === "inbound" ? "delivered" : "sent"),
      attachments: (() => { try { return typeof m.attachments === "string" ? JSON.parse(m.attachments) : (m.attachments || null); } catch { return null; } })(),
      _isEmailReply: m.direction === "inbound",
      _isEmailThread: true,
    })),
  [inboundEmails]);

  const sortByDate = (a, b) => {
    const da = a.followup_date ? new Date(a.followup_date).getTime() : 0;
    const db = b.followup_date ? new Date(b.followup_date).getTime() : 0;
    return db - da; // newest first
  };

  const mergedFollowUps = useMemo(() => [...inboundItems].sort(sortByDate), [inboundItems]);
  const mergedAllFollowUps = mergedFollowUps;

  // Auto-open the latest (top) item; re-open whenever a newer item arrives at [0]
  useEffect(() => { topItemIdRef.current = null; }, [lead.id]);
  useEffect(() => {
    if (mergedFollowUps.length === 0) return;
    const topId = String(mergedFollowUps[0].id);
    if (topId !== topItemIdRef.current) {
      topItemIdRef.current = topId;
      setExpandedIds(new Set([topId]));
    }
  }, [mergedFollowUps]);

  // Auto-scroll timeline to top (newest item) when items load/update
  useEffect(() => {
    const t = setTimeout(() => {
      if (timelineScrollRef.current) {
        timelineScrollRef.current.scrollTop = 0;
      }
    }, 100);
    return () => clearTimeout(t);
  }, [mergedFollowUps]);


  // // Auto-assign lead to logged-in user if not yet assigned
  // const autoAssignToSelf = useCallback(() => {
  //   if (lead.assign_to != null || isLeadAssigned.current) return;
  //   const userId = parseInt(localStorage.getItem("trade_user_id"), 10);
  //   if (!userId) return;
  //   allocateLeads({ internal_user_id: userId, lead_ids: [lead.id] }).catch(
  //     () => {},
  //   );
  // }, [lead.assign_to, lead.id]);

  const followUpOptionsList = FOLLOW_UP_STATUS_OPTIONS;

  // ── useUpdateEnquiryStatus for stage dropdown ──────────────────────────────
  const updateEnquiryStatus = useUpdateEnquiryStatus();

  // // ── Auto-assign: allocate lead to logged-in admin before first follow-up ──
  // const allocateLeadsMutation = useAllocateLeads();
  // const isLeadAssigned = useRef(lead.assign_to != null);

  // Email template hooks
  const { data: emailTemplates = [], isLoading: templatesLoading } =
    useEmailTemplates();
  const [isEmailSending, setIsEmailSending] = useState(false);

  // ── Helper: find option_value from label ───────────────────────────────────
  const getValueByLabel = useCallback(
    (label) => {
      const opt = followUpOptionsList.find((o) => o.label === label);
      return opt ? opt.option_value : "0";
    },
    [followUpOptionsList],
  );

  // ── Helper: find label from option_value ───────────────────────────────────
  const getLabelByValue = useCallback(
    (optionValue) => {
      const opt = followUpOptionsList.find(
        (o) => String(o.option_value) === String(optionValue),
      );
      return opt ? opt.label : "Follow Up";
    },
    [followUpOptionsList],
  );

  // ── Set initial follow-up status from lead data once options load ──────────
  useMemo(() => {
    if (followUpOptionsList.length > 0 && lead.follow_up_status !== undefined) {
      const numericVal = String(lead.follow_up_status);
      const matchByValue = followUpOptionsList.find(
        (o) => String(o.option_value) === numericVal,
      );
      if (matchByValue) {
        setFollowUpStatus(matchByValue.label);
      } else {
        const matchByLabel = followUpOptionsList.find(
          (o) => o.label === lead.follow_up_status,
        );
        if (matchByLabel) setFollowUpStatus(matchByLabel.label);
      }
    }
  }, [followUpOptionsList, lead.follow_up_status]);

  // ── Fetch fresh lead details on open → resolve label from status ────────────
  React.useEffect(() => {
    if (!followUpOptionsList.length) return;
    fetchLeadDetails(lead.id, { lead_type: 1 })
      .then((res) => {
        if (res?.data) {
          const label =
            getLabelByValue(String(res.data.status)) || res.data.lead_stage;
          if (label) setFollowUpStatus(label);
          setContact((prev) => ({
            ...prev,
            name: res.data.name || prev.name,
            email: res.data.email || prev.email,
            phone: res.data.phone || prev.phone,
            additional_phone:
              res.data.additional_phone ||
              res.data.phone2 ||
              prev.additional_phone ||
              "",
          }));
          // // Update isLeadAssigned ref from fresh data
          // if (res.data.assign_to != null) {
          //   isLeadAssigned.current = true;
          // }
        }
      })
      .catch(() => {});
  }, [lead.id, followUpOptionsList, getLabelByValue]);

  const selectedTemplate = useMemo(() => {
    if (!emailTemplate) return null;
    return (
      emailTemplates.find((t) => String(t.id) === String(emailTemplate)) || null
    );
  }, [emailTemplate, emailTemplates]);

  const handleTemplateChange = useCallback(
    async (templateId) => {
      setEmailTemplate(templateId);
      if (!templateId) {
        setEmailSubject("");
        setEmailBody("");
        setTemplateAttachments([]);
        return;
      }
      try {
        const preview = await fetchTemplatePreview(templateId, lead.id);
        setEmailSubject(preview.subject || "");
        setEmailBody(preview.body || "");
        setTemplateAttachments(parseAttachments(preview.attachments));
      } catch {
        const tpl = emailTemplates.find(
          (t) => String(t.id) === String(templateId),
        );
        if (tpl) {
          setEmailSubject(tpl.subject || "");
          setEmailBody(tpl.body || "");
          setTemplateAttachments(parseAttachments(tpl.attachments));
        }
      }
    },
    [emailTemplates, lead.id],
  );

  const initials = (n) =>
    n
      ?.split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";
  const buildNextFU = (date, time) => {
    if (!date) return {};
    return {
      next_followup_date: date,
      ...(time ? { next_followup_time: time } : {}),
    };
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddCcEmail = () => {
    const v = emailCcInput.trim();
    if (!v) return;
    if (!isValidEmail(v)) {
      setEmailCcError("Invalid email address");
      return;
    }
    if (emailCcList.includes(v)) {
      setEmailCcError("Email already added");
      return;
    }
    setEmailCcList((p) => [...p, v]);
    setEmailCcInput("");
    setEmailCcError("");
  };

  const handleAddBccEmail = () => {
    const v = emailBccInput.trim();
    if (!v) return;
    if (!isValidEmail(v)) {
      setEmailBccError("Invalid email address");
      return;
    }
    if (emailBccList.includes(v)) {
      setEmailBccError("Email already added");
      return;
    }
    setEmailBccList((p) => [...p, v]);
    setEmailBccInput("");
    setEmailBccError("");
  };

  const handleSendEmail = () => {
    if (!emailBody.trim()) return;

    // // ── Auto-assign guard: allocate unassigned lead before sending ──
    // if (!isLeadAssigned.current) {
    //   const uid = parseInt(localStorage.getItem("trade_user_id"), 10);
    //   if (uid) {
    //     allocateLeadsMutation.mutate(
    //       { adminId: uid, leadIds: [lead.id] },
    //       {
    //         onSuccess: () => {
    //           isLeadAssigned.current = true;
    //           handleSendEmail();
    //         },
    //       },
    //     );
    //     return;
    //   }
    // }

    const ccArr = emailCcList;
    const bccArr = emailBccList;
    const internalUserId = Number(localStorage.getItem("trade_user_id")) || "";

    if (selectedTemplate) {
      const sendTemplate = async () => {
        setIsEmailSending(true);
        try {
          const fd = new FormData();
          fd.append("template_id", selectedTemplate.id);
          fd.append("from", "info@entermark.ai");
          fd.append("to", emailTo);
          fd.append("customer_id", lead.id);
          fd.append("internal_user_id", internalUserId);
          fd.append("conversation_id", activeConversationId || "");
          emailFiles.forEach((f) => fd.append("attachments", f));

          await axios.post(
            `${API_BASE_URL}/subscription-inbox/send-using-template`,
            fd,
            { headers: { Authorization: `Bearer ${localStorage.getItem("cms_token")}` } },
          );
        } catch {
          toast.error("Failed to send email. Please try again.");
          setIsEmailSending(false);
          return;
        }

        // Refresh thread to show sent message
        toast.success("Email sent successfully!");
        await fetchThread(activeConversationId);
        resetEmailForm();
        setActivePanel(null);
        setIsEmailSending(false);
      };
      sendTemplate();
      return;
    }
    // No template — send via subscription-inbox/send API
    const sendAndLog = async () => {
      setIsEmailSending(true);
      try {
        const fd = new FormData();
        fd.append("from", "info@entermark.ai");
        fd.append("to", emailTo);
        fd.append("subject", emailSubject);
        fd.append("body", emailBody);
        fd.append("customer_id", lead.id);
        fd.append("internal_user_id", internalUserId);
        fd.append("conversation_id", activeConversationId || "");
        emailFiles.forEach((f) => fd.append("attachments", f));

        await axios.post(
          `${API_BASE_URL}/subscription-inbox/send`,
          fd,
          { headers: { Authorization: `Bearer ${localStorage.getItem("cms_token")}` } },
        );
      } catch {
        toast.error("Failed to send email. Please try again.");
        setIsEmailSending(false);
        return;
      }
      toast.success("Email sent successfully!");
      await fetchThread(activeConversationId);
      resetEmailForm();
      setActivePanel(null);
      setIsEmailSending(false);
    };

    sendAndLog();
  };

  const resetEmailForm = () => {
    setEmailBody("");
    setEmailSubject("");
    setEmailTemplate("");
    setEmailFiles([]);
    setTemplateAttachments([]);
    setEmailRemarks("");
    setEmailCcList([]);
    setEmailCcInput("");
    setEmailBccList([]);
    setEmailBccInput("");
    setEmailNextDate("");
    setEmailNextTime("");
    setEmailFuStatus("0");
    setShowEmailCc(false);
    setShowEmailBcc(false);
  };

  const handleLogCall = () => {
    // // ── Auto-assign guard: allocate unassigned lead before logging call ──
    // if (!isLeadAssigned.current) {
    //   const uid = parseInt(localStorage.getItem("trade_user_id"), 10);
    //   if (uid) {
    //     allocateLeadsMutation.mutate(
    //       { adminId: uid, leadIds: [lead.id] },
    //       {
    //         onSuccess: () => {
    //           isLeadAssigned.current = true;
    //           handleLogCall();
    //         },
    //       },
    //     );
    //     return;
    //   }
    // }

    setCallNote("");
    setCallOutcome("Connected");
    setCallDuration("");
    setCallNextDate("");
    setCallNextTime("");
    setCallFuStatus("0");
    setActivePanel(null);
  };

  const handleSaveNote = () => {
    if (!noteText.trim()) return;

    // // ── Auto-assign guard: allocate unassigned lead before saving note ──
    // if (!isLeadAssigned.current) {
    //   const uid = parseInt(localStorage.getItem("trade_user_id"), 10);
    //   if (uid) {
    //     allocateLeadsMutation.mutate(
    //       { adminId: uid, leadIds: [lead.id] },
    //       {
    //         onSuccess: () => {
    //           isLeadAssigned.current = true;
    //           handleSaveNote();
    //         },
    //       },
    //     );
    //     return;
    //   }
    // }

    setNoteText("");
    setNoteNextDate("");
    setNoteNextTime("");
    setActivePanel(null);
  };

  const addEmailFiles = (e) => {
    if (!e.target.files?.length) return;
    const newFiles = Array.from(e.target.files);
    if (emailFileRef.current) emailFileRef.current.value = "";
    setEmailFiles((prev) => [...prev, ...newFiles]);
  };

  React.useEffect(() => {
    if (
      activePanel === "email" &&
      emailFiles.length > 0 &&
      composeScrollRef.current
    ) {
      composeScrollRef.current.scrollTop =
        composeScrollRef.current.scrollHeight;
    }
  }, [emailFiles.length, activePanel]);
  const removeEmailFile = (idx) =>
    setEmailFiles((p) => p.filter((_, i) => i !== idx));
  const addWaFiles = (e) => {
    if (!e.target.files?.length) return;
    setWaFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    if (waFileRef.current) waFileRef.current.value = "";
  };
  const removeWaFile = (idx) =>
    setWaFiles((p) => p.filter((_, i) => i !== idx));

  const handleEditSave = (_payload) => {
    setEditingFU(null);
  };

  const handleDeleteFU = (_fu) => {
    // No-op: follow-up deletion not supported in subscription context
  };

  const syncLeadStage = (statusLabel) => {
    const optionValue = getValueByLabel(statusLabel);
    if (!optionValue || optionValue === "0") return;
    updateEnquiryStatus.mutate(
      { id: lead.id, status: Number(optionValue), message: "" },
      { onSuccess: () => setFollowUpStatus(statusLabel) },
    );
  };

  const handleStatusDropdownChange = (e) => {
    const v = e.target.value;
    if (v === followUpStatus) return;
    setPendingStatus(v);
    setShowStageModal(true);
  };

  const handleStageConfirm = (payload) => {
    const selectedLabel = payload.follow_up_status;
    const optionValue = getValueByLabel(selectedLabel);

    updateEnquiryStatus.mutate(
      {
        id: lead.id,
        status: Number(optionValue),
        message: payload.remarks || "",
      },
      {
        onSuccess: () => {
          setFollowUpStatus(selectedLabel);
          setShowStageModal(false);
          setPendingStatus(null);
        },
        onError: () => {
          setShowStageModal(false);
          setPendingStatus(null);
        },
      },
    );
  };

  const handleClickToCall = (clientNo, clientName) => {
    // agent_no = the logged-in user's own mobile; the helper also
    // reports an unregistered number instead of failing silently.
    placeClickToCall(clientNo, clientName);
  };

  const saveContact = async (d) => {
    setIsSavingContact(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/update-user-profile`,
        {
          id: lead.id,
          name: d.name,
          mobile: d.phone,
          alternate_number: d.additional_phone || "",
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("cms_token")}` } }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      setContact(d);
      setShowContactEdit(false);
      toast.success("Contact updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["enquiryList"] });
      queryClient.invalidateQueries({ queryKey: ["inbox-leads"] });
      queryClient.invalidateQueries({ queryKey: ["future-leads"] });
      queryClient.invalidateQueries({ queryKey: ["done-leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead-details", lead.id] });
    } catch (err) {
      toast.error(err?.message || "Failed to update contact");
    } finally {
      setIsSavingContact(false);
    }
  };

  const PANELS = [
    {
      key: "email",
      label: "Compose Mail",
      color: "#667eea",
      grad: "linear-gradient(135deg,#667eea,#764ba2)",
      shadow: "rgba(102,126,234,0.35)",
      Icon: IC.Mail,
    },
  ];

  const fuColor = statusColor(followUpStatus);
  const isSaving = false;
  const isTemplateSending = isEmailSending;
  const L = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    display: "block",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  };
  const inp = "fu-inp";

  return (
    <div
      className="main-content"
      style={{
        fontFamily: "'Segoe UI',system-ui,sans-serif",
        background: "#f1f5f9",
        height: "calc(100vh - 70px)",
        marginTop: "70px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{`
        .fu-i{transition:all 0.18s ease;cursor:pointer}.fu-i:hover{transform:translateY(-1px)}
        .fu-ab{display:inline-flex;align-items:center;gap:7px;padding:8px 18px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;border:none;transition:all 0.18s}.fu-ab:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(0,0,0,0.15)}
        .fu-inp{width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:9px;font-size:13px;outline:none;transition:border 0.2s;background:white;box-sizing:border-box}.fu-inp:focus{border-color:#667eea;box-shadow:0 0 0 3px rgba(102,126,234,0.1)}
        .fu-sel{width:100%;padding:9px 12px;border:1.5px solid #e2e8f0;border-radius:9px;font-size:13px;outline:none;background:white;cursor:pointer;box-sizing:border-box}
        .fu-ta{width:100%;padding:10px 12px;border:1.5px solid #e2e8f0;border-radius:9px;font-size:13px;outline:none;resize:vertical;min-height:80px;background:white;font-family:inherit;box-sizing:border-box}.fu-ta:focus{border-color:#667eea;box-shadow:0 0 0 3px rgba(102,126,234,0.1)}
        .fu-card{border-radius:14px;padding:14px 16px;border:1.5px solid;transition:all 0.2s}.fu-card:hover{transform:translateX(3px);box-shadow:0 6px 20px rgba(0,0,0,0.07)}
        @keyframes slideUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}.fu-anim{animation:slideUp 0.25s ease}
        .fu-pill{padding:5px 12px;border-radius:20px;font-size:12px;font-weight:600;border:1.5px solid;cursor:pointer;transition:all 0.15s}.fu-pill:hover{transform:translateY(-1px)}
        ::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#c7d2fe;border-radius:10px}
        .fu-section{border:1.5px solid #e8ecff;border-radius:14px;overflow:hidden;margin-bottom:0}
        .fu-sec-hdr{background:linear-gradient(135deg,#f8f9ff,#f0f4ff);padding:11px 14px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #e8ecff}
        .fu-nfd{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px;padding-top:10px;border-top:1px dashed #e2e8f0}
        .fu-nfd label{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:3px;display:flex;align-items:center;gap:3px}
        .fu-nfd input{padding:7px 10px;border:1.5px solid #e2e8f0;border-radius:7px;font-size:12px;outline:none;background:white;width:100%;box-sizing:border-box}
        .fu-files-row{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
        @keyframes modalIn{from{opacity:0;transform:scale(0.92) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}
      `}</style>

      <input
        type="file"
        multiple
        ref={emailFileRef}
        onChange={addEmailFiles}
        style={{ display: "none" }}
      />
      <input
        type="file"
        multiple
        ref={waFileRef}
        onChange={addWaFiles}
        style={{ display: "none" }}
      />

      {showStageModal && (
        <StageUpdateModal
          currentStatus={followUpStatus}
          initialStatus={pendingStatus}
          onSave={handleStageConfirm}
          onCancel={() => {
            setShowStageModal(false);
            setPendingStatus(null);
          }}
          isLoading={updateEnquiryStatus.isPending}
          followUpOptionsList={followUpOptionsList}
        />
      )}
      {editingFU && (
        <EditFollowUpModal
          fu={editingFU}
          onSave={handleEditSave}
          onCancel={() => setEditingFU(null)}
          isLoading={false}
          followUpOptionsList={followUpOptionsList}
        />
      )}
      {showContactEdit && (
        <ContactEditModal
          contact={contact}
          onSave={saveContact}
          onCancel={() => setShowContactEdit(false)}
          isLoading={isSavingContact}
        />
      )}
      {showPreview && selectedTemplate && (
        <EmailPreviewModal
          subject={selectedTemplate.subject}
          body={selectedTemplate.body}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* BREADCRUMB */}
      <div
        style={{
          padding: "0 24px",
          background: "white",
          borderBottom: "1px solid #e8ecff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          color: "#6b7280",
          flexShrink: 0,
          height: "48px",
        }}
      >
        <button
          onClick={handleClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "none",
            border: "none",
            color: "#667eea",
            fontWeight: 600,
            cursor: "pointer",
            fontSize: "13px",
            padding: 0,
          }}
        >
          <IC.ArrowLeft /> Back
        </button>
        <span style={{ color: "#c7d2fe", fontSize: "16px" }}>›</span>
        <span style={{ color: "#1f2937", fontWeight: 600 }}>{lead.name}</span>
        <span style={{ color: "#9ca3af" }}>[ID: {lead.id}]</span>
        {lead.stage_name && (
          <span
            style={{
              marginLeft: "6px",
              padding: "2px 10px",
              borderRadius: "20px",
              background: "#f0f4ff",
              color: "#667eea",
              fontSize: "11px",
              fontWeight: 700,
              border: "1px solid #c7d2fe",
            }}
          >
            {lead.stage_name}
          </span>
        )}
      </div>

      {/* TWO-COLUMN LAYOUT */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "300px 1fr",
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* ══ LEFT SIDEBAR ══ */}
        <div
          style={{
            background: "white",
            borderRight: "2px solid #f0f4ff",
            overflowY: "auto",
            padding: "16px 14px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            minHeight: 0,
            height: "100%",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg,#667eea,#764ba2)",
              borderRadius: "16px",
              padding: "18px 16px",
              color: "white",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "17px",
                  fontWeight: 700,
                  flexShrink: 0,
                  border: "2px solid rgba(255,255,255,0.4)",
                }}
              >
                {initials(lead.name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "16px", fontWeight: 700 }}>
                  {lead.name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    opacity: 0.8,
                    marginTop: "2px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lead.message || lead.service || "Enquiry"}
                </div>
              </div>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  fontSize: "12px",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "8px",
                  padding: "7px 10px",
                }}
              >
                <IC.Mail />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lead.email || "N/A"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  fontSize: "12px",
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "8px",
                  padding: "7px 10px",
                }}
              >
                <IC.Phone />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{lead.phone || "N/A"}</div>
                  {lead.additional_phone && (
                    <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "1px" }}>{lead.additional_phone}</div>
                  )}
                </div>
                {lead.phone && (
                  lead.additional_phone ? (
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [
                          {
                            key: "primary",
                            label: (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}>
                                <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}>
                                  <IC.Phone />
                                </div>
                                <div>
                                  <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div>
                                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{lead.phone}</div>
                                </div>
                              </div>
                            ),
                          },
                          {
                            key: "alternate",
                            label: (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}>
                                <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}>
                                  <IC.Phone />
                                </div>
                                <div>
                                  <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Alternate</div>
                                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{lead.additional_phone}</div>
                                </div>
                              </div>
                            ),
                          },
                        ],
                        onClick: ({ key }) => {
                          const num = key === "primary" ? lead.phone : lead.additional_phone;
                          handleClickToCall(num, lead.name);
                          setActivePanel("call");
                        },
                      }}
                    >
                      <button
                        className="fu-i"
                        style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#16a34a", border: "none", display: "flex", alignItems: "center", justifyContent: "center", color: "white", cursor: "pointer", flexShrink: 0 }}
                      >
                        <IC.Phone />
                      </button>
                    </Dropdown>
                  ) : (
                    <button
                      className="fu-i"
                      onClick={() => { handleClickToCall(lead.phone, lead.name); setActivePanel("call"); }}
                      style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#16a34a", border: "none", display: "flex", alignItems: "center", justifyContent: "center", color: "white", cursor: "pointer", flexShrink: 0 }}
                    >
                      <IC.Phone />
                    </button>
                  )
                )}
              </div>
              {lead.plan_name && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    padding: "7px 10px",
                  }}
                >
                  <IC.Layers />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {lead.plan_name}
                  </span>
                </div>
              )}
              {lead.is_attorney !== null && lead.is_attorney !== undefined && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    padding: "7px 10px",
                  }}
                >
                  <IC.Check />
                  {lead.is_attorney === 1 ? "Attorney / IP Professional" : "Corporate"}
                </div>
              )}
              {lead.plan_type && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    padding: "7px 10px",
                  }}
                >
                  <IC.Calendar />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "capitalize" }}>
                    {lead.plan_type}
                  </span>
                </div>
              )}
              {lead.company && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    padding: "7px 10px",
                  }}
                >
                  <IC.Building />
                  {lead.company}
                </div>
              )}
              {lead.address && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    fontSize: "12px",
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    padding: "7px 10px",
                  }}
                >
                  <IC.MapPin />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {lead.address}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="fu-section">
            <div className="fu-sec-hdr">
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#1f2937",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                }}
              >
                <IC.User /> Contact
              </div>
            </div>
            <div style={{ padding: "12px 14px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#1f2937",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contact.name}
                  </p>
                  <p
                    style={{
                      margin: "3px 0 0",
                      fontSize: "12px",
                      color: "#6b7280",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contact.email}
                  </p>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    {contact.phone}
                  </p>
                  {contact.additional_phone && (
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      {contact.additional_phone}{" "}
                      <span style={{ fontSize: "10px", color: "#9ca3af" }}>
                        (alt)
                      </span>
                    </p>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    flexShrink: 0,
                    alignSelf: "flex-start",
                    marginTop: "2px",
                  }}
                >
                  {contact.additional_phone ? (
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [
                          {
                            key: "primary",
                            label: (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}>
                                <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}>
                                  <IC.Phone />
                                </div>
                                <div>
                                  <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div>
                                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{contact.phone}</div>
                                </div>
                              </div>
                            ),
                          },
                          {
                            key: "alternate",
                            label: (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}>
                                <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}>
                                  <IC.Phone />
                                </div>
                                <div>
                                  <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Alternate</div>
                                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{contact.additional_phone}</div>
                                </div>
                              </div>
                            ),
                          },
                        ],
                        onClick: ({ key }) => {
                          const num = key === "primary" ? contact.phone : contact.additional_phone;
                          handleClickToCall(num, contact.name);
                          setActivePanel("call");
                        },
                      }}
                    >
                      <button
                        className="fu-i"
                        style={{ width: "26px", height: "26px", borderRadius: "7px", border: "1.5px solid #e8ecff", background: "#f8f9ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#667eea", cursor: "pointer", flexShrink: 0 }}
                      >
                        <IC.Phone />
                      </button>
                    </Dropdown>
                  ) : (
                    <Tooltip title="Call">
                      <button
                        className="fu-i"
                        onClick={() => { handleClickToCall(contact.phone, contact.name); setActivePanel("call"); }}
                        style={{ width: "26px", height: "26px", borderRadius: "7px", border: "1.5px solid #e8ecff", background: "#f8f9ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#667eea", cursor: "pointer", flexShrink: 0 }}
                      >
                        <IC.Phone />
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip title="Email">
                    <button
                      className="fu-i"
                      onClick={() => setActivePanel("email")}
                      style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "7px",
                        border: "1.5px solid #e8ecff",
                        background: "#f8f9ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#667eea",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      <IC.Mail />
                    </button>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <button
                      className="fu-i"
                      onClick={() => setShowContactEdit(true)}
                      style={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "7px",
                        border: "1.5px solid #e8ecff",
                        background: "#f8f9ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#667eea",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      <IC.Edit />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 2px 0",
              marginTop: "2px",
            }}
          >
            {/* <button
              onClick={() => {
                Swal.fire({
                  title: "Mark as Negative?",
                  input: "textarea",
                  inputLabel: "Remark",
                  inputPlaceholder: "Enter your remark...",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Yes, Mark Negative",
                  confirmButtonColor: "#dc2626",
                }).then((r) => {
                  if (r.isConfirmed) onNegativeClosed(r.value || "");
                });
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "none",
                border: "none",
                color: "#ef4444",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <IC.Negative /> Negative Closed
            </button> */}
          </div>
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "12px 22px",
              borderBottom: "2px solid #f0f4ff",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            {PANELS.map(({ key, label, color, grad, shadow, Icon }) => {
              const btnStyle = {
                display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 16px",
                borderRadius: "22px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                border: `2px solid ${color}25`, whiteSpace: "nowrap",
                background: activePanel === key ? grad : `${color}10`,
                color: activePanel === key ? "white" : color,
                boxShadow: activePanel === key ? `0 4px 14px ${shadow}` : "none",
                transition: "all 0.2s",
              };
              if (key === "call" && lead.additional_phone) {
                return (
                  <Dropdown
                    key={key}
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "primary",
                          label: (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}>
                              <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <IC.Phone style={{ color: "#16a34a", fontSize: "11px" }} />
                              </div>
                              <div>
                                <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{lead.phone}</div>
                              </div>
                            </div>
                          ),
                        },
                        {
                          key: "alternate",
                          label: (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}>
                              <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <IC.Phone style={{ color: "#2563eb", fontSize: "11px" }} />
                              </div>
                              <div>
                                <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Alternate</div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{lead.additional_phone}</div>
                              </div>
                            </div>
                          ),
                        },
                      ],
                      onClick: ({ key: numKey }) => {
                        const num = numKey === "primary" ? lead.phone : lead.additional_phone;
                        handleClickToCall(num, lead.name);
                        setActivePanel("call");
                      },
                    }}
                  >
                    <button className="fu-i" onClick={() => setActivePanel(activePanel === key ? null : key)} style={btnStyle}>
                      <Icon />{label}
                    </button>
                  </Dropdown>
                );
              }
              return (
                <button
                  key={key}
                  className="fu-i"
                  onClick={() => { if (key === "call" && activePanel !== "call") handleClickToCall(lead.phone, lead.name); setActivePanel(activePanel === key ? null : key); }}
                  style={btnStyle}
                >
                  <Icon />
                  {label}
                </button>
              );
            })}
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "4px 11px",
                  borderRadius: "20px",
                  background: `${fuColor}15`,
                  color: fuColor,
                  fontSize: "11px",
                  fontWeight: 700,
                  border: `1.5px solid ${fuColor}30`,
                  whiteSpace: "nowrap",
                }}
              >
                <IC.Layers />
                {followUpStatus}
              </span>
              <div style={{ position: "relative" }}>
                <select
                  value={followUpStatus}
                  onChange={handleStatusDropdownChange}
                  disabled={false}
                  style={{
                    minWidth: "145px",
                    padding: "7px 32px 7px 12px",
                    border: `1.5px solid ${fuColor}`,
                    borderRadius: "9px",
                    fontSize: "13px",
                    outline: "none",
                    background: "white",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    color: fuColor,
                    fontWeight: 700,
                    appearance: "none",
                    WebkitAppearance: "none",
                  }}
                >
                  {false && <option>Loading...</option>}
                  {followUpOptionsList.map((opt) => (
                    <option key={opt.id} value={opt.label}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: fuColor,
                    pointerEvents: "none",
                  }}
                >
                  <IC.ChevronDown />
                </div>
              </div>
            </div>
          </div>

          {/* COMPOSE PANELS */}
          {activePanel && (
            <div
              style={{ padding: "0 22px", marginTop: "14px", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}
            >
              <div
                className="fu-anim"
                style={{
                  background: "white",
                  borderRadius: "15px",
                  border: `2px solid ${getTypeCfg(activePanel).border}`,
                  overflow: "hidden",
                  boxShadow: `0 5px 24px ${getTypeCfg(activePanel).color}12`,
                  marginBottom: "4px",
                  display: "flex",
                  flexDirection: "column",
                  // height: "100%",
                  maxHeight: "calc(60vh - 30px)",
                }}
              >
                <div
                  style={{
                    background: PANELS.find((p) => p.key === activePanel).grad,
                    padding: "11px 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                    }}
                  >
                    <div
                      style={{
                        width: "30px",
                        height: "30px",
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      {React.createElement(
                        PANELS.find((p) => p.key === activePanel).Icon,
                      )}
                    </div>
                    <span
                      style={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      {PANELS.find((p) => p.key === activePanel).label}
                    </span>
                    {activePanel === "call" && (
                      <span
                        style={{
                          color: "rgba(255,255,255,.7)",
                          fontSize: "11px",
                        }}
                      >
                        {lead.name} ({lead.phone})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setActivePanel(null)}
                    style={{
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.2)",
                      border: "none",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <IC.X />
                  </button>
                </div>
                <div
                  ref={composeScrollRef}
                  style={{
                    padding: "16px 18px",
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {activePanel === "email" && (
                    <React.Fragment>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "11px",
                      }}
                    >
                      {(isSaving || isTemplateSending) && (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "10px 14px",
                          borderRadius: "10px",
                          background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                          border: "1px solid #86efac",
                          marginBottom: "2px",
                        }}>
                          <div style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border: "2.5px solid #16a34a",
                            borderTopColor: "transparent",
                            animation: "spin 0.75s linear infinite",
                            flexShrink: 0,
                          }} />
                          <span style={{ fontSize: "12px", color: "#15803d", fontWeight: 600 }}>
                            Sending email, please wait…
                          </span>
                        </div>
                      )}
                      <div
                        style={{
                          border: "1.5px solid #e2e8f0",
                          borderRadius: "9px",
                          overflow: "hidden",
                          background: "white",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            borderBottom:
                              showEmailCc || showEmailBcc
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <span
                            style={{
                              padding: "9px 12px",
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#6b7280",
                              minWidth: "48px",
                              flexShrink: 0,
                              borderRight: "1px solid #f0f0f0",
                            }}
                          >
                            To
                          </span>
                          <input
                            style={{
                              flex: 1,
                              padding: "9px 12px",
                              border: "none",
                              outline: "none",
                              fontSize: "13px",
                              background: "transparent",
                            }}
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            placeholder="recipient@email.com"
                          />
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              padding: "0 12px",
                              flexShrink: 0,
                            }}
                          >
                            {!showEmailCc && (
                              <button
                                type="button"
                                onClick={() => setShowEmailCc(true)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  cursor: "pointer",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  transition: "color 0.15s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.color = "#1f2937")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.color = "#6b7280")
                                }
                              >
                                Cc
                              </button>
                            )}
                            {!showEmailBcc && (
                              <button
                                type="button"
                                onClick={() => setShowEmailBcc(true)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  cursor: "pointer",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  transition: "color 0.15s",
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.color = "#1f2937")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.color = "#6b7280")
                                }
                              >
                                Bcc
                              </button>
                            )}
                          </div>
                        </div>
                        {showEmailCc && (
                          <div
                            style={{
                              borderBottom: showEmailBcc
                                ? "1px solid #f0f0f0"
                                : "none",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                              }}
                            >
                              <span
                                style={{
                                  padding: "10px 12px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  minWidth: "48px",
                                  flexShrink: 0,
                                  borderRight: "1px solid #f0f0f0",
                                }}
                              >
                                Cc
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  padding: "5px 8px",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  alignItems: "center",
                                }}
                              >
                                {emailCcList.map((e, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "3px",
                                      padding: "2px 10px",
                                      background: "#eff6ff",
                                      border: "1px solid #bfdbfe",
                                      borderRadius: "999px",
                                      fontSize: "12px",
                                      color: "#2563eb",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {e}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setEmailCcList((p) =>
                                          p.filter((_, j) => j !== i),
                                        )
                                      }
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#93c5fd",
                                        fontSize: "16px",
                                        lineHeight: 1,
                                        padding: "0 0 0 3px",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      &times;
                                    </button>
                                  </span>
                                ))}
                                <input
                                  autoFocus
                                  style={{
                                    flex: 1,
                                    minWidth: "160px",
                                    padding: "4px 2px",
                                    border: "none",
                                    outline: "none",
                                    fontSize: "13px",
                                    background: "transparent",
                                  }}
                                  value={emailCcInput}
                                  onChange={(e) => {
                                    setEmailCcInput(e.target.value);
                                    setEmailCcError("");
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                      e.preventDefault();
                                      handleAddCcEmail();
                                    }
                                  }}
                                  onBlur={handleAddCcEmail}
                                  placeholder={
                                    emailCcList.length === 0
                                      ? "cc@email.com (press Enter to add)"
                                      : ""
                                  }
                                />
                              </div>
                            </div>
                            {emailCcError && (
                              <div
                                style={{
                                  padding: "2px 12px 5px 60px",
                                  fontSize: "11px",
                                  color: "#ef4444",
                                }}
                              >
                                {emailCcError}
                              </div>
                            )}
                          </div>
                        )}
                        {showEmailBcc && (
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                              }}
                            >
                              <span
                                style={{
                                  padding: "10px 12px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#6b7280",
                                  minWidth: "48px",
                                  flexShrink: 0,
                                  borderRight: "1px solid #f0f0f0",
                                }}
                              >
                                Bcc
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  padding: "5px 8px",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  alignItems: "center",
                                }}
                              >
                                {emailBccList.map((e, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "3px",
                                      padding: "2px 10px",
                                      background: "#f5f3ff",
                                      border: "1px solid #ddd6fe",
                                      borderRadius: "999px",
                                      fontSize: "12px",
                                      color: "#7c3aed",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {e}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setEmailBccList((p) =>
                                          p.filter((_, j) => j !== i),
                                        )
                                      }
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#c4b5fd",
                                        fontSize: "16px",
                                        lineHeight: 1,
                                        padding: "0 0 0 3px",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      &times;
                                    </button>
                                  </span>
                                ))}
                                <input
                                  autoFocus
                                  style={{
                                    flex: 1,
                                    minWidth: "160px",
                                    padding: "4px 2px",
                                    border: "none",
                                    outline: "none",
                                    fontSize: "13px",
                                    background: "transparent",
                                  }}
                                  value={emailBccInput}
                                  onChange={(e) => {
                                    setEmailBccInput(e.target.value);
                                    setEmailBccError("");
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                      e.preventDefault();
                                      handleAddBccEmail();
                                    }
                                  }}
                                  onBlur={handleAddBccEmail}
                                  placeholder={
                                    emailBccList.length === 0
                                      ? "bcc@email.com (press Enter to add)"
                                      : ""
                                  }
                                />
                              </div>
                            </div>
                            {emailBccError && (
                              <div
                                style={{
                                  padding: "2px 12px 5px 60px",
                                  fontSize: "11px",
                                  color: "#ef4444",
                                }}
                              >
                                {emailBccError}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={L}>
                          Email Template{" "}
                          <span
                            style={{
                              color: "#9ca3af",
                              fontWeight: 400,
                              fontSize: "10px",
                              textTransform: "none",
                            }}
                          ></span>
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <select
                            className="fu-sel"
                            value={emailTemplate}
                            onChange={(e) =>
                              handleTemplateChange(e.target.value)
                            }
                            style={{ flex: 1 }}
                          >
                            <option value="">— No Template (Manual) —</option>
                            {templatesLoading && (
                              <option disabled>Loading templates...</option>
                            )}
                            {emailTemplates.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.template_name.replace(/_/g, " ")}
                              </option>
                            ))}
                          </select>
                          {selectedTemplate && (
                            <>
                              <Tooltip title="Preview Template">
                                <button
                                  className="fu-i"
                                  onClick={() => setShowPreview(true)}
                                  style={{
                                    width: "34px",
                                    height: "34px",
                                    borderRadius: "8px",
                                    border: "1.5px solid #c7d2fe",
                                    background: "#f0f4ff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#667eea",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                  }}
                                >
                                  <IC.Eye />
                                </button>
                              </Tooltip>
                              <Tooltip title="Remove Template">
                                <button
                                  className="fu-i"
                                  onClick={() => handleTemplateChange("")}
                                  style={{
                                    width: "34px",
                                    height: "34px",
                                    borderRadius: "8px",
                                    border: "1.5px solid #fca5a5",
                                    background: "#fff2f2",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#ef4444",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    fontSize: "16px",
                                    fontWeight: 700,
                                    lineHeight: 1,
                                  }}
                                >
                                  ×
                                </button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <label style={L}>Subject</label>
                        <input
                          className={inp}
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          placeholder="Subject..."
                          readOnly={!!selectedTemplate}
                          style={
                            selectedTemplate
                              ? { background: "#f8f9ff", color: "#6b7280" }
                              : {}
                          }
                        />
                      </div>
                      {selectedTemplate ? (
                        <div>
                          <label style={L}>
                            Body{" "}
                            <span
                              style={{
                                color: "#9ca3af",
                                fontWeight: 400,
                                fontSize: "10px",
                                textTransform: "none",
                              }}
                            >
                              (from template)
                            </span>
                          </label>
                          <div
                            style={{
                              padding: "12px",
                              background: "#f8f9ff",
                              borderRadius: "9px",
                              border: "1.5px solid #e8ecff",
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.6,
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                            dangerouslySetInnerHTML={{ __html: emailBody }}
                          />
                        </div>
                      ) : (
                        <div>
                          <label style={L}>Body</label>
                          <EmailRichEditor value={emailBody} onChange={setEmailBody} />
                        </div>
                      )}
                      <div>
                        <label style={L}>Remarks</label>
                        <input
                          className={inp}
                          value={emailRemarks}
                          onChange={(e) => setEmailRemarks(e.target.value)}
                          placeholder="Internal remarks..."
                        />
                      </div>
                      <div>
                        <label style={L}>Follow-up Status</label>
                        <FollowUpStatusSelect
                          value={emailFuStatus}
                          followUpOptionsList={followUpOptionsList}
                          onChange={(val) => {
                            setEmailFuStatus(val);
                          }}
                        />
                      </div>
                      <div className="fu-nfd">
                        <div>
                          <label>
                            <IC.Calendar /> Next Follow-up
                          </label>
                          <input
                            type="date"
                            value={emailNextDate}
                            onChange={(e) => setEmailNextDate(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                        <div>
                          <label>
                            <IC.Clock /> Time
                          </label>
                          <input
                            type="time"
                            value={emailNextTime}
                            onChange={(e) => setEmailNextTime(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                      </div>
                      {templateAttachments.length > 0 && (
                        <div style={{ marginTop: "4px" }}>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              marginBottom: "5px",
                            }}
                          >
                            Template Attachments
                          </div>
                          <div className="fu-files-row">
                            {templateAttachments.map((f, i) => (
                              <FileChip
                                key={`tpl-${i}`}
                                file={f}
                                onRemove={null}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {emailFiles.length > 0 && (
                        <div style={{ marginTop: "4px" }}>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#9ca3af",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              marginBottom: "5px",
                            }}
                          >
                            Added by You
                          </div>
                          <div className="fu-files-row">
                            {emailFiles.map((f, i) => (
                              <FileChip
                                key={i}
                                file={f}
                                onRemove={() => removeEmailFile(i)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    </React.Fragment>
                  )}

                  {activePanel === "call" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "11px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "11px",
                        }}
                      >
                        <div>
                          <label style={L}>Call Outcome</label>
                          <select
                            className="fu-sel"
                            value={callOutcome}
                            onChange={(e) => setCallOutcome(e.target.value)}
                          >
                            {CALL_OUTCOMES.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={L}>Call Duration</label>
                          <input
                            className={inp}
                            value={callDuration}
                            onChange={(e) => setCallDuration(e.target.value)}
                            placeholder="e.g. 15 mins"
                          />
                        </div>
                      </div>
                      <div>
                        <label style={L}>Call Notes</label>
                        <textarea
                          className="fu-ta"
                          placeholder="Notes about this call..."
                          value={callNote}
                          onChange={(e) => setCallNote(e.target.value)}
                          style={{ borderColor: "#fde68a", minHeight: "80px" }}
                        />
                      </div>
                      <div>
                        <label style={L}>Follow-up Status</label>
                        <FollowUpStatusSelect
                          value={callFuStatus}
                          followUpOptionsList={followUpOptionsList}
                          onChange={(val) => {
                            setCallFuStatus(val);
                          }}
                        />
                      </div>
                      <div className="fu-nfd">
                        <div>
                          <label>
                            <IC.Calendar /> Next Follow-up
                          </label>
                          <input
                            type="date"
                            value={callNextDate}
                            onChange={(e) => setCallNextDate(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                        <div>
                          <label>
                            <IC.Clock /> Time
                          </label>
                          <input
                            type="time"
                            value={callNextTime}
                            onChange={(e) => setCallNextTime(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activePanel === "note" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "11px",
                      }}
                    >
                      <div>
                        <label style={L}>Internal Note</label>
                        <textarea
                          className="fu-ta"
                          placeholder="Write your note... (visible only to team)"
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          style={{ borderColor: "#ddd6fe", minHeight: "100px" }}
                        />
                      </div>
                      <div className="fu-nfd">
                        <div>
                          <label>
                            <IC.Calendar /> Next Follow-up
                          </label>
                          <input
                            type="date"
                            value={noteNextDate}
                            onChange={(e) => setNoteNextDate(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                        <div>
                          <label>
                            <IC.Clock /> Time
                          </label>
                          <input
                            type="time"
                            value={noteNextTime}
                            onChange={(e) => setNoteNextTime(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* ── STICKY FOOTER: action buttons always visible ── */}
                <div
                  style={{
                    padding: "10px 18px",
                    borderTop: "1px solid #f0f4ff",
                    background: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "8px",
                    flexShrink: 0,
                  }}
                >
                  {activePanel === "email" && (
                    <>
                      <button
                        className="fu-ab fu-i"
                        onClick={() => emailFileRef.current?.click()}
                        style={{
                          background: "#f0f4ff",
                          color: "#667eea",
                          border: "1.5px solid #c7d2fe",
                          marginRight: "auto",
                        }}
                      >
                        <IC.Attach /> Attach Files
                      </button>
                      <button
                        className="fu-ab fu-i"
                        onClick={() => setActivePanel(null)}
                        style={{ background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0" }}
                      >
                        Cancel
                      </button>
                      <button
                        className="fu-ab fu-i"
                        disabled={isSaving || isTemplateSending || (!selectedTemplate && !emailBody.trim())}
                        onClick={handleSendEmail}
                        style={{
                          background: "linear-gradient(135deg,#667eea,#764ba2)",
                          color: "white",
                          boxShadow: "0 4px 12px rgba(102,126,234,0.35)",
                          opacity: isSaving || isTemplateSending ? 0.7 : 1,
                        }}
                      >
                        <IC.Send />
                        {isSaving || isTemplateSending ? "Sending..." : "Send"}
                      </button>
                    </>
                  )}
                  {activePanel === "call" && (
                    <>
                      <button
                        className="fu-ab fu-i"
                        onClick={() => setActivePanel(null)}
                        style={{ background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0" }}
                      >
                        Cancel
                      </button>
                      <button
                        className="fu-ab fu-i"
                        disabled={isSaving}
                        onClick={handleLogCall}
                        style={{
                          background: "linear-gradient(135deg,#f59e0b,#d97706)",
                          color: "white",
                          boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
                          opacity: isSaving ? 0.7 : 1,
                        }}
                      >
                        <IC.Check />
                        {isSaving ? "Logging..." : "Log Call"}
                      </button>
                    </>
                  )}
                  {activePanel === "note" && (
                    <>
                      <button
                        className="fu-ab fu-i"
                        onClick={() => setActivePanel(null)}
                        style={{ background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0" }}
                      >
                        Cancel
                      </button>
                      <button
                        className="fu-ab fu-i"
                        disabled={isSaving}
                        onClick={handleSaveNote}
                        style={{
                          background: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                          color: "white",
                          boxShadow: "0 4px 12px rgba(139,92,246,0.3)",
                          opacity: isSaving ? 0.7 : 1,
                        }}
                      >
                        <IC.Note />
                        {isSaving ? "Saving..." : "Save Note"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── TIMELINE ── */}
          <div
            ref={timelineScrollRef}
            style={{ flex: 1, overflowY: "auto", padding: "14px 22px 80px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <IC.Filter /> Filter:
              </span>
              {["All", "Email", "Call", "Note"].map((f) => {
                const count =
                  f === "All"
                    ? mergedAllFollowUps.length
                    : mergedAllFollowUps.filter((fu) => {
                        const type = fu.followup_type?.toLowerCase();
                        if (f === "Email") return type === "email";
                        if (f === "Call") return type === "call";
                        if (f === "Note")
                          return type === "other" || type === "note";
                        return false;
                      }).length;
                return (
                  <button
                    key={f}
                    className="fu-pill fu-i"
                    onClick={() => setActiveFilter(f)}
                    style={{
                      background:
                        activeFilter === f
                          ? "linear-gradient(135deg,#667eea,#764ba2)"
                          : "white",
                      color: activeFilter === f ? "white" : "#6b7280",
                      borderColor: activeFilter === f ? "#667eea" : "#e2e8f0",
                    }}
                  >
                    {f}
                    <span style={{ opacity: 0.7 }}> ({count})</span>
                  </button>
                );
              })}
            </div>

            {emailThreadLoading && (
              <div style={{ padding: "12px 0", display: "flex", flexDirection: "column", gap: "10px" }}>
                {[1, 2, 3].map((i) => (
                  <div key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                    }} />
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{
                        height: 12, borderRadius: 6, width: `${55 + i * 10}%`,
                        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                      }} />
                      <div style={{
                        height: 10, borderRadius: 6, width: "40%",
                        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                      }} />
                    </div>
                  </div>
                ))}
                <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
              </div>
            )}

            {emailThreadLoading && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 14px",
                borderRadius: "12px",
                background: "linear-gradient(135deg,#eff6ff,#e0f2fe)",
                border: "1px solid #bae6fd",
                marginBottom: "8px",
              }}>
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  border: "2.5px solid #0284c7",
                  borderTopColor: "transparent",
                  animation: "spin 0.75s linear infinite",
                  flexShrink: 0,
                }} />
                <span style={{ fontSize: "12px", color: "#0369a1", fontWeight: 600 }}>
                  Loading email threads…
                </span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {!emailThreadLoading &&
              mergedFollowUps.map((fu, idx) => {
                const cfg = getTypeCfg(fu.followup_type);
                const isIn = fu.direction === "inbound";
                return (
                  <div
                    key={fu.id}
                    className="fu-anim"
                    style={{
                      display: "flex",
                      gap: "12px",
                      marginBottom: "4px",
                      flexDirection: isIn ? "row-reverse" : "row",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: isIn
                            ? "linear-gradient(135deg,#06b6d4,#0284c7)"
                            : "linear-gradient(135deg,#667eea,#764ba2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "11px",
                          fontWeight: 700,
                          border: "3px solid white",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          zIndex: 1,
                        }}
                      >
                        {isIn
                          ? initials(lead.name)
                          : fu.created_by_name
                            ? initials(fu.created_by_name)
                            : "SA"}
                      </div>
                      {idx < mergedFollowUps.length - 1 && (
                        <div
                          style={{
                            width: "2px",
                            flex: 1,
                            background:
                              "linear-gradient(to bottom,#e8ecff,transparent)",
                            margin: "4px 0",
                            minHeight: "18px",
                          }}
                        />
                      )}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        maxWidth: "calc(100% - 56px)",
                        marginBottom: "12px",
                      }}
                    >
                      {expandedIds.has(String(fu.id)) ? (
                        <div
                          onClick={() => setExpandedIds((prev) => {
                            const next = new Set(prev);
                            const key = String(fu.id);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            return next;
                          })}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                            marginBottom: "6px",
                            flexWrap: "wrap",
                            flexDirection: isIn ? "row-reverse" : "row",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "3px 9px",
                              borderRadius: "20px",
                              background: cfg.bg,
                              color: cfg.color,
                              fontSize: "11px",
                              fontWeight: 700,
                              border: `1.5px solid ${cfg.border}`,
                            }}
                          >
                            <cfg.Icon />
                            {cfg.label}
                            {fu.whatsapp_msg_type &&
                              fu.whatsapp_msg_type !== "text" && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    marginLeft: "2px",
                                  }}
                                >
                                  {getMediaIcon(fu.whatsapp_msg_type)}
                                </span>
                              )}
                          </span>
                          <span style={{ fontSize: "11px", color: "#6b7280" }}>
                            {formatDateTime(fu.followup_date)}
                          </span>
                          {fu._isEmailReply && (
                            <span style={{ fontSize: 11, background: "#e0f2fe", color: "#0369a1", borderRadius: 4, padding: "1px 6px" }}>
                              Reply
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: "11px",
                              color: isIn ? "#0284c7" : "#667eea",
                              fontWeight: 700,
                            }}
                          >
                            {isIn ? lead.name : fu.created_by_name || "Admin"}
                          </span>
                          <span style={{ marginLeft: "auto", fontSize: "12px", color: "#9ca3af" }}>▲</span>
                        </div>
                      ) : (
                        <div
                          onClick={() => setExpandedIds((prev) => {
                            const next = new Set(prev);
                            next.add(String(fu.id));
                            return next;
                          })}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "9px 12px",
                            background: "white",
                            border: `1.5px solid ${isIn ? "#bae6fd" : "#e8ecff"}`,
                            borderRadius: "10px",
                            cursor: "pointer",
                            userSelect: "none",
                            marginBottom: "4px",
                            minWidth: 0,
                          }}
                        >
                          <span style={{ fontWeight: 700, fontSize: "13px", color: isIn ? "#0284c7" : "#374151", flexShrink: 0 }}>
                            {isIn ? lead.name : (fu.created_by_name || "Me")}
                          </span>
                          <span style={{ flex: 1, fontSize: "13px", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                            {fu.followup_type === "email"
                              ? (fu.content || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim()
                              : (fu.content || fu.call_outcome || "")}
                          </span>
                          {fu.delivery_status && (
                            <span style={{ fontSize: "11px", color: fu.delivery_status === "delivered" ? "#16a34a" : "#667eea", fontWeight: 600, flexShrink: 0 }}>
                              {fu.delivery_status === "delivered" ? "Delivered" : fu.delivery_status === "sent" ? "Sent" : fu.delivery_status}
                            </span>
                          )}
                          <span style={{ fontSize: "11px", color: "#9ca3af", flexShrink: 0 }}>{formatDateTime(fu.followup_date)}</span>
                          <span style={{ fontSize: "12px", color: "#9ca3af", flexShrink: 0 }}>▼</span>
                        </div>
                      )}
                      {expandedIds.has(String(fu.id)) && <div
                        className="fu-card"
                        style={{
                          background: isIn ? "#f0f9ff" : cfg.bg,
                          borderColor: isIn ? "#bae6fd" : cfg.border,
                        }}
                      >
                        {fu.subject && (
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: "13px",
                              color: "#1f2937",
                              marginBottom: "7px",
                              paddingBottom: "7px",
                              borderBottom: `1px solid ${cfg.border}`,
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <IC.Mail />
                            {fu.subject}
                          </div>
                        )}
                        {fu.followup_type === "call" && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "3px 10px",
                                borderRadius: "20px",
                                background:
                                  fu.call_outcome === "Connected" ||
                                  fu.status === "completed"
                                    ? "#f0fdf4"
                                    : "#fef2f2",
                                color:
                                  fu.call_outcome === "Connected" ||
                                  fu.status === "completed"
                                    ? "#16a34a"
                                    : "#dc2626",
                                fontSize: "11px",
                                fontWeight: 700,
                                border: `1.5px solid ${fu.call_outcome === "Connected" || fu.status === "completed" ? "#bbf7d0" : "#fecaca"}`,
                              }}
                            >
                              <IC.Audio />
                              {fu.call_outcome || fu.status}
                              {fu.call_duration && ` · ${fu.call_duration}`}
                            </span>
                            {fu.status === "completed" && <AudioWave />}
                          </div>
                        )}
                        {(fu._isEmailThread || (fu.followup_type === "email" && /<[a-z][\s\S]*>/i.test(fu.content || ""))) ? (
                          <>
                          <iframe
                            srcDoc={(() => {
                              const html = (fu.content || "")
                                .replace(/<blockquote[\s\S]*?<\/blockquote>/gi, "")
                                .replace(/<div[^>]*gmail_quote[\s\S]*?<\/div>/gi, "");
                              // Wrap plain text in basic HTML so it renders nicely
                              const isHtml = /<[a-z][\s\S]*>/i.test(html);
                              const reset = `<style>body{margin:0!important;padding:0!important}</style>`;
                              return isHtml
                                ? html.replace(/<head>/i, `<head>${reset}`).replace(/^(?![\s\S]*<head>)/, `<head>${reset}</head>`)
                                : `<html><head>${reset}</head><body style="font-family:Arial,sans-serif;font-size:14px;color:#374151;line-height:1.6;margin:0;padding:0;white-space:pre-wrap;">${html}</body></html>`;
                            })()}
                            style={{ width: "100%", border: "none", display: "block" }}
                            scrolling="no"
                            onLoad={(e) => {
                              const doc = e.target.contentDocument;
                              if (doc) e.target.style.height = doc.body.scrollHeight + "px";
                            }}
                            title="email-body"
                            sandbox="allow-same-origin"
                          />
                          {fu._isEmailReply && <div style={{
                            marginTop: 14,
                            paddingTop: 12,
                            borderTop: "1px solid #e8f0fe",
                            display: "flex",
                            justifyContent: "flex-end",
                          }}>
                            <button
                              onClick={() => {
                                setEmailSubject(fu.subject ? `Re: ${fu.subject.replace(/^Re:\s*/i, "")}` : "");
                                setActivePanel("email");
                              }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 7,
                                padding: "8px 20px",
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: 0.2,
                                background: "linear-gradient(135deg, #0078d4 0%, #00bcf2 100%)",
                                color: "#fff",
                                border: "none",
                                borderRadius: 8,
                                cursor: "pointer",
                                boxShadow: "0 4px 14px rgba(0,120,212,0.35)",
                                transition: "opacity 0.15s",
                              }}
                              onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                            >
                              <i className="bx bx-reply-all" style={{ fontSize: 16 }} />
                              Reply
                            </button>
                          </div>}
                          </>
                        ) : (
                          <p
                            style={{
                              margin: 0,
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.65,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {fu.content}
                          </p>
                        )}
                        {parseAttachments(fu.attachments).map((att, i) => {
                          const fType = getFileType(att.url || att.name);
                          return (
                            <div
                              key={i}
                              style={{
                                marginTop: "9px",
                                borderRadius: "10px",
                                overflow: "hidden",
                                border: `1.5px solid ${cfg.border}`,
                                background: "white",
                              }}
                            >
                              {fType === "image" && att.url && (
                                <a
                                  href={att.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "block",
                                    cursor: "pointer",
                                  }}
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name}
                                    style={{
                                      width: "100%",
                                      maxHeight: "220px",
                                      objectFit: "cover",
                                      display: "block",
                                      borderBottom: `1px solid ${cfg.border}`,
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                </a>
                              )}
                              {fType === "video" && att.url && (
                                <video
                                  controls
                                  preload="metadata"
                                  style={{
                                    width: "100%",
                                    maxHeight: "220px",
                                    display: "block",
                                    borderBottom: `1px solid ${cfg.border}`,
                                    background: "#000",
                                  }}
                                >
                                  <source src={att.url} />
                                  Your browser does not support video.
                                </video>
                              )}
                              {fType === "audio" && att.url && (
                                <div
                                  style={{
                                    padding: "10px 12px",
                                    borderBottom: `1px solid ${cfg.border}`,
                                    background: "#fafafa",
                                  }}
                                >
                                  <audio
                                    controls
                                    preload="metadata"
                                    style={{ width: "100%", height: "36px" }}
                                  >
                                    <source src={att.url} />
                                  </audio>
                                </div>
                              )}
                              {fType === "pdf" && att.url && (
                                <a
                                  href={att.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "block",
                                    padding: "14px 12px",
                                    borderBottom: `1px solid ${cfg.border}`,
                                    background: "#fef2f2",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "40px",
                                        height: "48px",
                                        background: "#dc2626",
                                        borderRadius: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        flexShrink: 0,
                                      }}
                                    >
                                      PDF
                                    </div>
                                    <div>
                                      <p
                                        style={{
                                          margin: 0,
                                          fontSize: "12px",
                                          fontWeight: 600,
                                          color: "#1f2937",
                                        }}
                                      >
                                        Click to view PDF
                                      </p>
                                      <p
                                        style={{
                                          margin: "2px 0 0",
                                          fontSize: "11px",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {att.name}
                                      </p>
                                    </div>
                                  </div>
                                </a>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "9px",
                                  padding: "8px 12px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "7px",
                                    background: cfg.bg,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: cfg.color,
                                    flexShrink: 0,
                                  }}
                                >
                                  {fType === "image" ? (
                                    <IC.Image />
                                  ) : (
                                    <FileTypeIcon type={fType} />
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: "#1f2937",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {att.name}
                                  </p>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: "10px",
                                      color: "#9ca3af",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {fType}
                                    {att.size ? ` · ${att.size}` : ""}
                                  </p>
                                </div>
                                {att.url && (
                                  <button
                                    onClick={() =>
                                      forceDownload(att.url, att.name)
                                    }
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      padding: "5px 12px",
                                      background: cfg.bg,
                                      color: cfg.color,
                                      border: `1.5px solid ${cfg.border}`,
                                      borderRadius: "7px",
                                      fontSize: "11px",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      transition: "all 0.15s",
                                      cursor: "pointer",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        cfg.color;
                                      e.currentTarget.style.color = "white";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = cfg.bg;
                                      e.currentTarget.style.color = cfg.color;
                                    }}
                                  >
                                    ↓ Download
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {fu.next_followup_date && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "6px 10px",
                              background: "#fffbeb",
                              borderRadius: "8px",
                              border: "1px solid #fde68a",
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              fontSize: "11px",
                              color: "#d97706",
                              fontWeight: 600,
                            }}
                          >
                            <IC.Clock /> Next: {formatNextFollowUp(fu)}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "7px",
                          }}
                        >
                          <div style={{ display: "flex", gap: "4px" }}>
                            <Tooltip title="Delete">
                              <button
                                className="fu-i"
                                onClick={() => handleDeleteFU(fu)}
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "6px",
                                  border: "1.5px solid #fecaca",
                                  background: "#fef2f2",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                }}
                              >
                                <IC.Trash />
                              </button>
                            </Tooltip>
                          </div>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                              fontSize: "10px",
                              color:
                                fu.delivery_status === "read"
                                  ? "#16a34a"
                                  : "#9ca3af",
                              fontWeight: 500,
                            }}
                          >
                            {fu.delivery_status === "read" ? (
                              <>
                                <IC.DblCheck />
                                &nbsp;Read
                              </>
                            ) : fu.delivery_status === "delivered" ? (
                              <>
                                <IC.Check />
                                &nbsp;Delivered
                              </>
                            ) : fu.delivery_status === "sent" ? (
                              <>
                                <IC.Check />
                                &nbsp;Sent
                              </>
                            ) : fu.status === "completed" ? (
                              "✓ Done"
                            ) : (
                              fu.status
                            )}
                          </span>
                        </div>
                      </div>}
                    </div>
                  </div>
                );
              })}
            {!emailThreadLoading && mergedFollowUps.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "50px 20px",
                  color: "#9ca3af",
                }}
              >
                <div style={{ fontSize: "44px", marginBottom: "10px" }}>💬</div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#6b7280",
                  }}
                >
                  No follow-ups yet
                </div>
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                  Use the compose buttons above to create your first follow-up
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerFollowUp;